import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";
import { app } from "../firebase";
import MyTasselPlusUpsellModal from "../Widgets/MyTasselPlusUpsellModal";
import { createMyTasselPlusCheckoutSession } from "../services/MyTasselPlusCheckoutService";
import {
  MYTASSEL_PLUS_CONFIG_DOC,
  MYTASSEL_PLUS_ENTITLEMENTS_COLLECTION,
  MYTASSEL_PLUS_TIERS,
  canAddMyTasselPlusItems,
  canUseMyTasselPlusFeature,
  getMyTasselPlusAccess,
  getMyTasselPlusFeatureLimit,
} from "../constants/MyTasselPlus";

const useMyTasselPlus = () => {
  const db = useMemo(() => getFirestore(app), []);
  const [entitlement, setEntitlement] = useState(null);
  const [rolloutConfig, setRolloutConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upsellFeature, setUpsellFeature] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [authUid, setAuthUid] = useState(() => getAuth(app).currentUser?.uid || null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(app), (user) => {
      setAuthUid(user?.uid || null);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    let mounted = true;
    let configReady = false;
    let entitlementReady = !authUid;

    const updateLoading = () => {
      if (mounted) setLoading(!(configReady && entitlementReady));
    };

    setLoading(true);

    const unsubscribeConfig = onSnapshot(
      doc(db, MYTASSEL_PLUS_CONFIG_DOC.collection, MYTASSEL_PLUS_CONFIG_DOC.id),
      (configSnap) => {
        if (!mounted) return;
        setRolloutConfig(configSnap.exists() ? configSnap.data() : null);
        configReady = true;
        updateLoading();
      },
      () => {
        if (!mounted) return;
        setRolloutConfig(null);
        configReady = true;
        updateLoading();
      }
    );

    let unsubscribeEntitlement = () => {};
    const uid = authUid;

    if (uid) {
      entitlementReady = false;
      unsubscribeEntitlement = onSnapshot(
        doc(db, MYTASSEL_PLUS_ENTITLEMENTS_COLLECTION, uid),
        (entitlementSnap) => {
          if (!mounted) return;
          setEntitlement(entitlementSnap.exists() ? entitlementSnap.data() : null);
          entitlementReady = true;
          updateLoading();
        },
        () => {
          if (!mounted) return;
          setEntitlement(null);
          entitlementReady = true;
          updateLoading();
        }
      );
    } else {
      setEntitlement(null);
      entitlementReady = true;
      updateLoading();
    }

    return () => {
      mounted = false;
      unsubscribeConfig();
      unsubscribeEntitlement();
    };
  }, [db, authUid]);

  const access = useMemo(() => {
    if (loading) {
      return {
        tier: MYTASSEL_PLUS_TIERS.STANDARD,
        hasPlus: false,
        source: "loading",
        entitlement: null,
        reason: "entitlement_loading",
      };
    }

    return getMyTasselPlusAccess({ entitlement, rolloutConfig });
  }, [entitlement, loading, rolloutConfig]);

  const closeUpsell = useCallback(() => {
    setUpsellFeature(null);
    setCheckoutError("");
  }, []);

  const startCheckout = useCallback(async () => {
    if (checkoutLoading) return false;

    setCheckoutError("");

    if (access.hasPlus) {
      closeUpsell();
      return true;
    }

    const authUser = getAuth(app).currentUser;

    if (!authUser || authUser.isAnonymous) {
      setCheckoutError("Please sign in before upgrading to MyTassel+.");
      return false;
    }

    setCheckoutLoading(true);

    try {
      const result = await createMyTasselPlusCheckoutSession(authUser);

      if (result.alreadyOwned) {
        closeUpsell();
        return true;
      }

      if (result.url && typeof window !== "undefined") {
        window.location.assign(result.url);
        return true;
      }

      throw new Error("Checkout did not return a Stripe redirect URL.");
    } catch (error) {
      setCheckoutError(error.message || "Unable to start MyTassel+ checkout.");
      return false;
    } finally {
      setCheckoutLoading(false);
    }
  }, [access.hasPlus, checkoutLoading, closeUpsell]);

  const canUseFeature = useCallback(
    (feature) => canUseMyTasselPlusFeature(access, feature),
    [access]
  );

  const getFeatureLimit = useCallback(
    (feature) => getMyTasselPlusFeatureLimit(access, feature),
    [access]
  );

  const canAddItems = useCallback(
    (feature, currentCount = 0, addCount = 1) => canAddMyTasselPlusItems(access, feature, currentCount, addCount),
    [access]
  );

  const openUpsell = useCallback((feature) => {
    setCheckoutError("");
    setUpsellFeature(feature || "plus");
  }, []);

  const requireFeature = useCallback((feature, action) => {
    if (canUseMyTasselPlusFeature(access, feature)) {
      action?.();
      return true;
    }
    openUpsell(feature);
    return false;
  }, [access, openUpsell]);

  const requireItemCapacity = useCallback((feature, currentCount = 0, addCount = 1, action) => {
    if (canAddMyTasselPlusItems(access, feature, currentCount, addCount)) {
      action?.();
      return true;
    }
    openUpsell(feature);
    return false;
  }, [access, openUpsell]);

  const upsellModal = (
    <MyTasselPlusUpsellModal
      isVisible={Boolean(upsellFeature)}
      feature={upsellFeature}
      onClose={closeUpsell}
      onCta={startCheckout}
      isLoading={checkoutLoading}
      errorMessage={checkoutError}
    />
  );

  return {
    access,
    entitlement,
    rolloutConfig,
    loading,
    hasPlus: access.hasPlus,
    checkoutLoading,
    checkoutError,
    canUseFeature,
    getFeatureLimit,
    canAddItems,
    openUpsell,
    closeUpsell,
    startCheckout,
    requireFeature,
    requireItemCapacity,
    upsellModal,
  };
};

export default useMyTasselPlus;
